import React, { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { getPushStatus, enableOfficePush, disableOfficePush, sendTestPush } from "../lib/webpush";

export default function OfficeAlertsButton() {
  const [status, setStatus] = useState("off"); // on | off | denied | needs-install | unsupported
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    getPushStatus().then(setStatus);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enableOfficePush();
      setStatus("on");
      const r = await sendTestPush();
      toast({ title: "Alerts on", description: `This device will buzz on new orders & reservations. Test sent to ${r.devices} device${r.devices === 1 ? "" : "s"}.` });
    } catch (e) {
      toast({ title: "Couldn't enable alerts", description: e.message });
      setStatus(await getPushStatus());
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await disableOfficePush();
      setStatus("off");
      toast({ title: "Alerts off", description: "This device will no longer receive push alerts." });
    } finally {
      setBusy(false);
    }
  };

  if (status === "unsupported") return null;

  const on = status === "on";
  const label = busy ? "Working…" : on ? "Alerts On" : status === "denied" ? "Alerts Blocked" : "Enable Alerts";
  const Icon = busy ? Loader2 : on ? BellRing : status === "denied" ? BellOff : Bell;

  return (
    <button
      onClick={on ? disable : enable}
      disabled={busy || status === "denied"}
      data-testid="office-alerts-toggle"
      title={
        status === "denied"
          ? "Notifications are blocked in browser settings for this site."
          : status === "needs-install"
          ? "On iPad: Add to Home Screen first, then enable from the installed app."
          : on
          ? "Tap to turn off push alerts on this device"
          : "Get buzzed when a new order or reservation lands"
      }
      className={`inline-flex items-center gap-2 px-4 py-2 border text-xs tracking-[0.2em] uppercase transition-colors disabled:opacity-50 ${
        on
          ? "border-[var(--mm-accent)] text-[var(--mm-accent)]"
          : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
      }`}
    >
      <Icon size={14} className={busy ? "animate-spin" : ""} />
      <span>{label}</span>
    </button>
  );
}
